import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { readCookieConsent, saveCookieConsent, COOKIE_CONSENT_EVENT } from "@/lib/cookie-consent";

export { COOKIE_CONSENT_STORAGE_KEY, COOKIE_CONSENT_EVENT } from "@/lib/cookie-consent";

export default function CookieConsentBanner({ onOpenSettings = () => {} }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const sync = () => {
      try {
        const { answered } = readCookieConsent();
        setVisible(!answered);
      } catch {
        setVisible(true);
      }
    };
    sync();
    window.addEventListener(COOKIE_CONSENT_EVENT, sync);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, sync);
  }, []);

  const accept = useCallback(() => {
    saveCookieConsent({ analytics: true, functional: true });
    setVisible(false);
  }, []);

  const reject = useCallback(() => {
    saveCookieConsent({ analytics: false, functional: false });
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-desc"
      data-cookie-banner
      className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-[max(1rem,env(safe-area-inset-left))] z-[110] w-[min(calc(100vw-2rem),24rem)] animate-in fade-in slide-in-from-bottom-4 duration-300"
    >
      <div className="relative overflow-hidden rounded-2xl border border-violet-light/90 bg-white shadow-[0_24px_64px_-16px_rgba(74,58,138,0.28),0_0_0_1px_rgba(238,235,255,0.8)] ring-1 ring-violet-fuerte/5">
        <div
          aria-hidden
          className="pointer-events-none absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-transparent via-gold/90 to-transparent"
        />
        <div className="p-4 sm:p-5">
          <div className="flex gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-light text-violet-fuerte ring-1 ring-violet-fuerte/10">
              <Cookie className="h-5 w-5" strokeWidth={2} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <p id="cookie-banner-title" className="font-display text-[15px] font-bold text-ink">
                Uso de cookies
              </p>
              <p id="cookie-banner-desc" className="mt-1.5 text-[13px] leading-relaxed text-ink-2">
                Usamos cookies necesarias y, con tu permiso, analíticas y funcionales. Puedes aceptar, rechazar las opcionales
                o configurar al detalle.
              </p>
              <Link
                to="/cookies"
                className="mt-2 inline-flex text-[13px] font-semibold text-violet-fuerte underline decoration-violet-fuerte/35 underline-offset-2 transition-colors hover:text-violet-oscuro hover:decoration-violet-oscuro/40"
              >
                Política de Cookies
              </Link>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-2">
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              <button
                type="button"
                onClick={reject}
                className="inline-flex h-10 min-h-[44px] flex-1 items-center justify-center rounded-xl border border-violet-light bg-[#FAFAFE] px-4 text-[14px] font-semibold text-ink-2 transition-colors hover:border-violet-fuerte/30 hover:bg-white hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-fuerte sm:flex-initial"
              >
                Rechazar opcionales
              </button>
              <button
                type="button"
                onClick={onOpenSettings}
                className="inline-flex h-10 min-h-[44px] flex-1 items-center justify-center rounded-xl border border-violet-fuerte/35 bg-white px-4 text-[14px] font-semibold text-violet-fuerte transition-colors hover:bg-violet-light/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-fuerte sm:flex-initial"
              >
                Configurar
              </button>
              <button
                type="button"
                onClick={accept}
                className="inline-flex h-10 min-h-[44px] flex-1 items-center justify-center rounded-xl px-4 text-[14px] font-semibold text-ink shadow-[0_4px_14px_-4px_rgba(74,58,138,0.35)] transition-[filter,transform] hover:brightness-105 active:scale-[0.99] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-fuerte sm:flex-initial sm:min-w-[7.5rem]"
                style={{
                  background: "linear-gradient(180deg, #E8D78A 0%, #C9A227 100%)",
                }}
              >
                Aceptar todas
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
